/* eslint-disable */
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { X, Loader2, Stethoscope, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// Advisor-facing "fill it in with the customer on the phone" version of the
// diagnostic pre-work questionnaire. Same questions, same storage path as the
// public/customer-facing form — but rendered inside the app so Steve doesn't
// have to text a link and wait for the customer to remember to fill it in.
export default function AdvisorQuestionnaireModal({ jobId, open, onClose, onSaved, customerName }) {
  const [rows, setRows] = useState(null);
  const [answers, setAnswers] = useState({});
  const [dataConsent, setDataConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);

  useEffect(() => {
    if (!open) return;
    setRows(null);
    api.get(`/jobs/${jobId}/questionnaire`)
      .then((r) => {
        setRows(r.data.rows || []);
        const seed = {};
        (r.data.rows || []).forEach((row) => { seed[row.id] = row.answer || ""; });
        setAnswers(seed);
        setSubmittedAt(r.data.submitted_at);
        setDataConsent(!!r.data?.consent?.data_processing);
        setMarketingConsent(!!r.data?.consent?.marketing);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load the questionnaire."));
  }, [open, jobId]);

  const close = () => { onClose?.(); };

  const setAnswer = (id, val) => setAnswers((prev) => ({ ...prev, [id]: val }));

  const save = async () => {
    if (!dataConsent) {
      toast.error("Confirm the customer gave verbal consent for the video health check.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/jobs/${jobId}/questionnaire`, {
        answers,
        consent_data_processing: dataConsent,
        consent_marketing: marketingConsent,
      });
      setSubmittedAt(r.data.submitted_at);
      toast.success("Questionnaire saved — the technician will see it on the job.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the questionnaire.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-6" data-testid="advisor-questionnaire-modal">
      <div className="w-full max-w-2xl bg-card border border-border rounded-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="border-b border-border px-5 py-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-warning/15 text-warning grid place-items-center shrink-0">
            <Stethoscope className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-overline text-warning">Pre-work questionnaire · advisor mode</div>
            <div className="font-display font-bold text-lg leading-tight mt-0.5">
              Fill in with {customerName || "the customer"}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Ask them each question on the call and tick / type the answer. Saves straight onto the job.
            </p>
          </div>
          <button type="button" onClick={close} className="p-2 hover:bg-muted rounded-md" data-testid="advisor-questionnaire-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {!rows ? (
            <div className="py-10 flex items-center justify-center text-muted-foreground gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading questions…
            </div>
          ) : (
            <>
              {submittedAt && (
                <div className="text-xs text-success bg-success/10 border border-success/30 rounded-md px-3 py-2">
                  Previously answered {new Date(submittedAt).toLocaleString("en-GB")} — you can edit and re-save.
                </div>
              )}
              {rows.map((row) => (
                <div key={row.id} data-testid={`advq-row-${row.id}`}>
                  <label className="text-sm font-medium block mb-1.5">
                    {row.label}
                    {row.required && <span className="text-destructive ml-1">*</span>}
                  </label>
                  {row.type === "select" && Array.isArray(row.options) ? (
                    <div className="flex flex-wrap gap-1.5">
                      {row.options.map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setAnswer(row.id, opt)}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                            answers[row.id] === opt
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background/40 text-muted-foreground border-border hover:bg-muted"
                          }`}
                          data-testid={`advq-opt-${row.id}-${opt.replace(/\s+/g,'-').toLowerCase()}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <textarea
                      rows={2}
                      value={answers[row.id] || ""}
                      onChange={(e) => setAnswer(row.id, e.target.value)}
                      className="input-default text-sm w-full resize-none"
                      placeholder={row.required ? "Required" : "Optional"}
                      data-testid={`advq-input-${row.id}`}
                    />
                  )}
                </div>
              ))}

              <div className="border-t border-border pt-4 space-y-2">
                <label className="flex items-start gap-2 text-xs cursor-pointer" data-testid="advq-consent-data">
                  <input
                    type="checkbox"
                    checked={dataConsent}
                    onChange={(e) => setDataConsent(e.target.checked)}
                    className="mt-0.5 rounded border-border accent-warning"
                  />
                  <span>
                    <strong>Verbal consent confirmed</strong> — the customer has agreed we can text and email them
                    the video health check for this booking.
                  </span>
                </label>
                <label className="flex items-start gap-2 text-xs cursor-pointer" data-testid="advq-consent-marketing">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="mt-0.5 rounded border-border accent-warning"
                  />
                  <span>Optional: they'd also like service / MOT reminders in future.</span>
                </label>
              </div>
            </>
          )}
        </div>

        <div className="border-t border-border px-5 py-3 flex items-center gap-3 justify-end">
          <button type="button" onClick={close} className="btn-outline text-sm" data-testid="advq-cancel">Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !rows}
            className="btn-primary text-sm inline-flex items-center gap-2"
            data-testid="advq-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Save questionnaire
          </button>
        </div>
      </div>
    </div>
  );
}
